import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, Sparkles, Star, X } from 'lucide-react';
import { lawyerPartnersService } from '../../services/lawyerPartnersService';
import { formatDocumentValue, formatOabValue, formatPhoneValue, normalizeDocumentValue, normalizeOabValue, normalizePhoneValue } from '../../utils/lawyerPartners';

const emptyForm = {
  nome: '',
  oab: '',
  email: '',
  telefone: '',
  documento: '',
  rating: 4,
  observacoes: '',
  negotiatesHonorarios: false,
};

function normalizeCandidate(candidate = {}) {
  return {
    nome: candidate?.nome || candidate?.name || '',
    oab: normalizeOabValue(candidate?.oab || ''),
    emails: Array.isArray(candidate?.emails)
      ? candidate.emails
      : candidate?.email
        ? [candidate.email]
        : [],
    telefones: Array.isArray(candidate?.telefones)
      ? candidate.telefones.map((item) => normalizePhoneValue(item)).filter(Boolean)
      : candidate?.telefone
        ? [normalizePhoneValue(candidate.telefone)]
        : [],
    documentos: Array.isArray(candidate?.documentos)
      ? candidate.documentos.map((item) => normalizeDocumentValue(item)).filter(Boolean)
      : candidate?.document || candidate?.documento
        ? [normalizeDocumentValue(candidate.document || candidate.documento)]
        : [],
    type: candidate?.type || null,
  };
}



function RatingStarsInput({ value = 1, onChange }) {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }).map((_, index) => {
          const starValue = index + 1;
          const active = starValue <= Number(value || 0);

          return (
            <button
              key={starValue}
              type="button"
              onClick={() => onChange(starValue)}
              className={`rounded-lg p-1.5 transition ${active ? 'text-amber-500' : 'text-gray-300 hover:text-amber-400'}`}
              aria-label={`Definir rating ${starValue} estrela${starValue > 1 ? 's' : ''}`}
              title={`${starValue} estrela${starValue > 1 ? 's' : ''}`}
            >
              <Star className={`h-6 w-6 ${active ? 'fill-current' : ''}`} />
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-sm font-medium text-amber-900">
        {value} estrela{Number(value) > 1 ? 's' : ''}
      </p>
    </div>
  );
}

function dedupeCandidates(candidates = []) {
  const map = new Map();

  candidates.forEach((candidate) => {
    const normalized = normalizeCandidate(candidate);
    const key = `${(normalized.nome || '').trim().toUpperCase()}::${(normalized.oab || '').trim().toUpperCase()}`;

    if (!normalized.nome || map.has(key)) return;
    map.set(key, normalized);
  });

  return Array.from(map.values());
}

export default function QuickLawyerPartnerModal({
  open,
  onClose,
  sourceLabel = '',
  fonteCadastro = 'PUBLICACAO',
  initialLawyers = [],
  onCreated,
}) {
  const [form, setForm] = useState(emptyForm);
  const [candidates, setCandidates] = useState([]);
  const [loadingEnrichment, setLoadingEnrichment] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const normalizedInitialLawyers = useMemo(
    () => dedupeCandidates(initialLawyers || []),
    [initialLawyers]
  );

  useEffect(() => {
    if (!open) return;

    setError('');
    setSuccess('');

    if (normalizedInitialLawyers.length > 0) {
      setCandidates(normalizedInitialLawyers);
      const first = normalizedInitialLawyers[0];
      setForm({
        nome: first?.nome || '',
        oab: normalizeOabValue(first?.oab || ''),
        email: first?.emails?.[0] || '',
        telefone: normalizePhoneValue(first?.telefones?.[0] || ''),
        documento: normalizeDocumentValue(first?.documentos?.[0] || ''),
        rating: 4,
        observacoes: sourceLabel ? `Cadastrado a partir de ${sourceLabel}` : '',
        negotiatesHonorarios: first?.negotiatesHonorarios ?? false,
      });
      return;
    }

    setCandidates([]);
    setForm({
      ...emptyForm,
      observacoes: sourceLabel ? `Cadastrado a partir de ${sourceLabel}` : '',
    });
  }, [open, normalizedInitialLawyers, sourceLabel]);

  if (!open) return null;

  const applyCandidate = (candidate) => {
    const normalized = normalizeCandidate(candidate);

    setForm((prev) => ({
      ...prev,
      nome: normalized?.nome || prev.nome,
      oab: normalized?.oab || prev.oab,
      email: normalized?.emails?.[0] || prev.email,
      telefone: normalized?.telefones?.[0] || prev.telefone,
      documento: normalized?.documentos?.[0] || prev.documento,
    }));
  };

  const handleEnrich = async () => {
    if (!form.documento?.trim()) {
      setError('Informe um CPF/CNPJ encontrado na publicação para consultar o DateSolutions.');
      return;
    }

    try {
      setLoadingEnrichment(true);
      setError('');
      const result = await lawyerPartnersService.enrichFromDocument(form.documento);
      setForm((prev) => ({
        ...prev,
        email: prev.email || result?.email || '',
        telefone: prev.telefone || normalizePhoneValue(result?.telefone || ''),
      }));
      setSuccess('Dados enriquecidos via DateSolutions.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao consultar o DateSolutions.');
    } finally {
      setLoadingEnrichment(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError('');
      const payload = {
        ...form,
        rating: Number(form.rating),
        fonteCadastro,
        metadata: {
          sourceLabel,
          origem: 'quick-lawyer-partner-modal',
        },
      };
      const created = await lawyerPartnersService.createPartner(payload);
      setSuccess('Advogado parceiro cadastrado com sucesso.');
      onCreated?.(created);
      setTimeout(() => onClose?.(), 800);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao salvar advogado parceiro.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-2xl rounded-2xl border border-gray-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Adicionar advogado parceiro</h3>
            <p className="text-sm text-gray-600">
              Pré-cadastro com base nos dados encontrados em {sourceLabel || 'uma origem identificada'}.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-1 px-6 py-3">
          {normalizedInitialLawyers.length > 0 ? (
            <div className="flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
              <Loader2 className="h-4 w-4" />
              Preparando advogado identificado{sourceLabel ? ` em ${sourceLabel}` : ''}...
            </div>
          ) : (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              Nenhum advogado foi pré-identificado. Você ainda pode preencher o cadastro manualmente.
            </div>
          )}

          {candidates.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Sparkles className="h-4 w-4 text-blue-600" />
                {normalizedInitialLawyers.length > 0 ? 'Advogados identificados' : 'Candidatos encontrados'}
              </div>
              <div className="flex flex-wrap gap-2">
                {candidates.map((candidate) => (
                  <button
                    key={`${candidate.nome}-${candidate.oab || 'sem-oab'}`}
                    type="button"
                    onClick={() => applyCandidate(candidate)}
                    className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-left text-sm text-gray-700 transition hover:border-blue-400 hover:bg-blue-50"
                  >
                    <div className="font-medium text-gray-900">{candidate.nome}</div>
                    <div className="mt-1 space-y-0.5 text-xs text-gray-500">
                      {candidate.oab ? <div>OAB {formatOabValue(candidate.oab)}</div> : null}
                      {candidate.documentos?.[0] ? <div>Documento {formatDocumentValue(candidate.documentos[0])}</div> : null}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <form className="space-y-4" onSubmit={handleSave}>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-1 text-sm">
                <span className="font-medium text-gray-700">Nome</span>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-gray-900 placeholder:text-gray-400"
                  value={form.nome}
                  onChange={(e) => setForm((prev) => ({ ...prev, nome: e.target.value }))}
                  required
                />
              </label>

              <label className="space-y-1 text-sm">
                <span className="font-medium text-gray-700">OAB</span>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-gray-900 placeholder:text-gray-400"
                  value={formatOabValue(form.oab)}
                  onChange={(e) => setForm((prev) => ({ ...prev, oab: normalizeOabValue(e.target.value) }))}
                  placeholder="Ex.: SP 123456"
                />
              </label>

              <label className="space-y-1 text-sm">
                <span className="font-medium text-gray-700">Email</span>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-gray-900 placeholder:text-gray-400"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                  type="email"
                />
              </label>

              <label className="space-y-1 text-sm">
                <span className="font-medium text-gray-700">Telefone</span>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-gray-900 placeholder:text-gray-400"
                  type="tel"
                  inputMode="numeric"
                  value={formatPhoneValue(form.telefone)}
                  onChange={(e) => setForm((prev) => ({ ...prev, telefone: normalizePhoneValue(e.target.value) }))}
                  placeholder="(11) 98765-4321"
                />
              </label>

              <label className="space-y-1 text-sm">
                <span className="font-medium text-gray-700">Documento (CPF/CNPJ)</span>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-gray-900 placeholder:text-gray-400"
                  inputMode="numeric"
                  value={formatDocumentValue(form.documento)}
                  onChange={(e) => setForm((prev) => ({ ...prev, documento: normalizeDocumentValue(e.target.value) }))}
                  placeholder="CPF ou CNPJ"
                />
              </label>

              <div className="space-y-1 text-sm">
                <span className="font-medium text-gray-700">Rating</span>
                <RatingStarsInput
                  value={form.rating}
                  onChange={(value) => setForm((prev) => ({ ...prev, rating: Number(value) }))}
                />
              </div>
            </div>

            <label className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
              <div>
                <div className="text-sm font-semibold text-gray-900">Negocia honorários</div>
                <div className="mt-1 text-xs text-gray-600">
                  Marque quando o parceiro negocia honorários na originação.
                </div>
              </div>
              <button
                type="button"
                onClick={() =>
                  setForm((prev) => ({ ...prev, negotiatesHonorarios: !prev.negotiatesHonorarios }))
                }
                className={`relative h-7 w-12 rounded-full border transition ${
                  form.negotiatesHonorarios
                    ? 'border-green-300 bg-green-500'
                    : 'border-gray-300 bg-gray-200'
                }`}
                aria-pressed={form.negotiatesHonorarios}
                aria-label="Alternar negociação de honorários"
              >
                <span
                  className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition ${
                    form.negotiatesHonorarios ? 'left-5' : 'left-0.5'
                  }`}
                />
              </button>
            </label>

            <label className="space-y-1 text-sm block">
              <span className="font-medium text-gray-700">Observações</span>
              <textarea
                className="min-h-[90px] w-full rounded-lg border border-gray-300 px-3 py-1 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-gray-900 placeholder:text-gray-400"
                value={form.observacoes}
                onChange={(e) => setForm((prev) => ({ ...prev, observacoes: e.target.value }))}
              />
            </label>

            {error ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
            ) : null}
            {success ? (
              <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">{success}</div>
            ) : null}

            <div className="flex flex-col gap-3 border-t border-gray-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={handleEnrich}
                disabled={loadingEnrichment}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-1 text-sm font-medium text-blue-700 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loadingEnrichment ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Buscar email/telefone no DateSolutions
              </button>

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Salvar parceiro
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
